import type {
  AuthChangeEvent,
  Session as SupabaseSession,
  User,
} from "@supabase/supabase-js";
import {
  AuditEntrySchema,
  OfferSkuSchema,
  PaperKingdomProjectSchema,
  type AuditEntry,
  type OfferSku,
  type PaperKingdomProject,
} from "../domain/schema";
import { productKey } from "../lib/productRoute";
import {
  isSupabaseConfigured,
  supabase,
} from "./supabaseClient";

export interface CloudWorkspace {
  role: "buyer" | "admin";
  projects: PaperKingdomProject[];
  entitlements: OfferSku[];
  allEntitlements: Record<string, OfferSku[]>;
  auditLog: AuditEntry[];
}

function requireClient() {
  if (!supabase || !isSupabaseConfigured) {
    throw new Error("Supabase is not configured for this build.");
  }
  return supabase;
}

function normalizeEmail(email: string) {
  return email.trim().toLowerCase();
}

function appUrl(path: string, search = "") {
  const url = new URL(path, window.location.origin);
  url.search = search.startsWith("?") ? search.slice(1) : search;
  return url.toString();
}

function parseProjectRow(
  row: Record<string, unknown>,
  ownerEmail: string,
): PaperKingdomProject | null {
  const payload =
    row.payload && typeof row.payload === "object" && !Array.isArray(row.payload)
      ? row.payload
      : null;
  if (!payload) return null;

  const parsed = PaperKingdomProjectSchema.safeParse({
    ...payload,
    id: row.id,
    ownerEmail,
    name: row.name,
    schemaVersion: row.schema_version,
    productVersion: row.product_version,
    revision: row.revision,
    updatedAt: row.client_updated_at,
  });
  return parsed.success ? parsed.data : null;
}

function mapAuditRow(row: Record<string, unknown>): AuditEntry | null {
  const action =
    row.action === "entitlement_granted"
      ? "grant"
      : row.action === "entitlement_revoked"
        ? "revoke"
        : "invite";
  const parsed = AuditEntrySchema.safeParse({
    id: row.id,
    actorEmail: "admin@aiagentoffer.com",
    targetEmail: row.target_email,
    action,
    offerSku: row.offer_sku,
    createdAt: row.created_at,
  });
  return parsed.success ? parsed.data : null;
}

export const cloudPortalRepository = {
  configured: isSupabaseConfigured,

  async getInitialSession(): Promise<SupabaseSession | null> {
    const client = requireClient();
    const { data, error } = await client.auth.getSession();
    if (error) throw error;
    return data.session;
  },

  onAuthStateChange(
    callback: (
      event: AuthChangeEvent,
      session: SupabaseSession | null,
    ) => void,
  ) {
    const client = requireClient();
    return client.auth.onAuthStateChange((event, session) =>
      callback(event, session),
    );
  },

  async registerWithPassword(email: string, password: string) {
    const client = requireClient();
    const { data, error } = await client.auth.signUp({
      email: normalizeEmail(email),
      password,
      options: {
        emailRedirectTo: appUrl("/login", "?confirmed=1"),
      },
    });
    if (error) throw error;
    if (!data.session && data.user?.identities?.length === 0) {
      throw new Error("An account already exists for this email.");
    }
    return { needsEmailConfirmation: !data.session };
  },

  async signInWithPassword(email: string, password: string) {
    const client = requireClient();
    const { error } = await client.auth.signInWithPassword({
      email: normalizeEmail(email),
      password,
    });
    if (error) throw error;
  },

  async requestPasswordReset(email: string) {
    const client = requireClient();
    const { error } = await client.auth.resetPasswordForEmail(
      normalizeEmail(email),
      { redirectTo: appUrl("/reset-password", "?recovery=1") },
    );
    if (error) throw error;
  },

  async updatePassword(password: string) {
    const client = requireClient();
    const { error } = await client.auth.updateUser({ password });
    if (error) throw error;
  },

  async signOut() {
    const client = requireClient();
    const { error } = await client.auth.signOut();
    if (error) throw error;
  },

  async loadWorkspace(user: User): Promise<CloudWorkspace> {
    const client = requireClient();
    const email = normalizeEmail(user.email ?? "");
    const [
      membershipsResult,
      entitlementsResult,
      projectsResult,
      auditResult,
    ] = await Promise.all([
      client
        .from("product_memberships")
        .select("email, role, status")
        .eq("product_key", productKey),
      client
        .from("product_entitlements")
        .select("email, offer_sku, active")
        .eq("product_key", productKey),
      client
        .from("product_projects")
        .select(
          "id, name, schema_version, product_version, revision, payload, client_updated_at",
        )
        .eq("product_key", productKey)
        .order("updated_at", { ascending: false }),
      client
        .from("product_audit_log")
        .select("id, target_email, action, offer_sku, created_at")
        .eq("product_key", productKey)
        .order("created_at", { ascending: false })
        .limit(100),
    ]);

    const firstError = [
      membershipsResult.error,
      entitlementsResult.error,
      projectsResult.error,
      auditResult.error,
    ].find(Boolean);
    if (firstError) throw firstError;

    const role = membershipsResult.data?.some(
      (membership) =>
        membership.status === "active" && membership.role === "admin",
    )
      ? "admin"
      : "buyer";

    const allEntitlements: Record<string, OfferSku[]> = {};
    for (const entitlement of entitlementsResult.data ?? []) {
      if (!entitlement.active) continue;
      const parsedSku = OfferSkuSchema.safeParse(entitlement.offer_sku);
      if (!parsedSku.success) continue;
      const targetEmail = normalizeEmail(entitlement.email);
      allEntitlements[targetEmail] = [
        ...(allEntitlements[targetEmail] ?? []),
        parsedSku.data,
      ];
    }

    return {
      role,
      projects: (projectsResult.data ?? [])
        .map((row) => parseProjectRow(row, email))
        .filter((project): project is PaperKingdomProject => Boolean(project)),
      entitlements: allEntitlements[email] ?? [],
      allEntitlements,
      auditLog: (auditResult.data ?? [])
        .map((row) => mapAuditRow(row))
        .filter((entry): entry is AuditEntry => Boolean(entry)),
    };
  },

  async saveProject(
    user: User,
    project: PaperKingdomProject,
  ) {
    const client = requireClient();
    const email = normalizeEmail(user.email ?? project.ownerEmail);
    const payload: PaperKingdomProject = {
      ...project,
      ownerEmail: email,
    };
    const { error } = await client.from("product_projects").upsert(
      {
        id: project.id,
        product_key: productKey,
        owner_id: user.id,
        name: project.name,
        schema_version: project.schemaVersion,
        product_version: project.productVersion,
        revision: project.revision,
        payload,
        client_updated_at: project.updatedAt,
      },
      { onConflict: "id" },
    );
    if (error) throw error;
  },

  async deleteProject(projectId: string) {
    const client = requireClient();
    const { error } = await client
      .from("product_projects")
      .delete()
      .eq("product_key", productKey)
      .eq("id", projectId);
    if (error) throw error;
  },

  async grantOffer(
    actor: User,
    email: string,
    sku: OfferSku,
    role: "buyer" | "admin" = "buyer",
  ) {
    const client = requireClient();
    const targetEmail = normalizeEmail(email);
    const existingMembership = await client
      .from("product_memberships")
      .select("role")
      .eq("product_key", productKey)
      .eq("email", targetEmail)
      .maybeSingle();
    if (existingMembership.error) throw existingMembership.error;

    const membership = await client.from("product_memberships").upsert(
      {
        product_key: productKey,
        email: targetEmail,
        role: existingMembership.data?.role === "admin" ? "admin" : role,
        status: "active",
        source: "manual",
        granted_by: actor.id,
      },
      { onConflict: "product_key,email" },
    );
    if (membership.error) throw membership.error;

    const entitlement = await client.from("product_entitlements").upsert(
      {
        product_key: productKey,
        email: targetEmail,
        offer_sku: sku,
        active: true,
        source: "manual",
        granted_by: actor.id,
      },
      { onConflict: "product_key,email,offer_sku" },
    );
    if (entitlement.error) throw entitlement.error;
  },

  async revokeOffer(email: string, sku: OfferSku) {
    const client = requireClient();
    const targetEmail = normalizeEmail(email);
    const { error } = await client
      .from("product_entitlements")
      .update({ active: false, source: "manual" })
      .eq("product_key", productKey)
      .eq("email", targetEmail)
      .eq("offer_sku", sku);
    if (error) throw error;

    const remaining = await client
      .from("product_entitlements")
      .select("id")
      .eq("product_key", productKey)
      .eq("email", targetEmail)
      .eq("active", true)
      .limit(1);
    if (remaining.error) throw remaining.error;
    if (remaining.data?.length) return;

    const membership = await client
      .from("product_memberships")
      .update({ status: "suspended", source: "manual" })
      .eq("product_key", productKey)
      .eq("email", targetEmail)
      .eq("role", "buyer");
    if (membership.error) throw membership.error;
  },
};
